import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  Button,
  Table,
  Empty,
  Space,
  Select,
  Input,
  DatePicker,
  Statistic,
  Card,
  Row,
  Col
} from 'antd'
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons'
import PageLoader from '../../components/Layouts/PageLoader'
import PrivateRoute from '../../hocs/PrivateRoute'
import PaginationContainer from '../../components/Forms/Pagination'
import PaymentTimelineModal from '../../components/PaymentTimelineModal'
import LogPaymentModal from './LogPaymentModal'
import RefundModal from './RefundModal'
import {
  getRequest,
  formatUrl,
  dutyTransactionsUrl,
  dutyTransactionSummaryUrl
} from '../../config'
import { columns } from './tableColumns'
import {
  STATUS_OPTIONS,
  PAYMENT_MODE_OPTIONS,
  GATEWAY_OPTIONS,
  RAIL_OPTIONS,
  buildQuery,
  formatMoney
} from './utils'

const { RangePicker } = DatePicker

const TransactionsList = props => {
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  // Overall successful revenue (all payments, or the date-filtered range) — from the backend, not
  // the loaded page.
  const [summary, setSummary] = useState({ revenue: 0, count: 0, currency: 'GHS' })
  // Draft filters (edited in the bar) vs. applied filters (sent on Apply/Refresh).
  const [status, setStatus] = useState(undefined)
  const [paymentMode, setPaymentMode] = useState(undefined)
  // Which company took the money, and on which way of paying (spec: payment-gateway-routing
  // Req 4.1). Filterable because the question they answer — "did everything on Bachs settle
  // today?" — is asked on the day something looks wrong.
  const [gateway, setGateway] = useState(undefined)
  const [rail, setRail] = useState(undefined)
  const [search, setSearch] = useState('')
  const [range, setRange] = useState(null)

  // Overall successful revenue, narrowed only by the from/to date filter (status/search ignored).
  const getSummary = async (filters = { range }) => {
    const params = new URLSearchParams()
    if (filters.range && filters.range[0]) {
      params.append('created_after', filters.range[0].toISOString())
    }
    if (filters.range && filters.range[1]) {
      params.append('created_before', filters.range[1].toISOString())
    }
    const qs = params.toString()
    const res = await getRequest(
      `${dutyTransactionSummaryUrl}${qs ? `?${qs}` : ''}`,
      'summary',
      () => {}
    )
    if (res.success) setSummary(res.data)
  }

  // `url` is a paginator next/previous link when paging (already carries filters + page);
  // otherwise build from current filters and load page 1.
  const getTransactions = async (
    filters = { status, search, range, paymentMode, gateway, rail },
    url = null
  ) => {
    const response = await getRequest(
      url ? formatUrl(url) : `${dutyTransactionsUrl}${buildQuery(filters)}`,
      'transactions',
      setLoading
    )
    if (response.success) {
      setTransactions(response.data.results || response.data)
      setMeta({ ...response.data })
    }
    // Refresh the overall revenue on a fresh load / filter change (not on pagination).
    if (!url) getSummary(filters)
  }

  useEffect(() => {
    getTransactions({
      status: undefined,
      search: '',
      range: null,
      paymentMode: undefined,
      gateway: undefined,
      rail: undefined
    })
    // eslint-disable-next-line
  }, [])

  const applyFilters = () =>
    getTransactions({ status, search, range, paymentMode, gateway, rail })

  const clearFilters = () => {
    setStatus(undefined)
    setPaymentMode(undefined)
    setGateway(undefined)
    setRail(undefined)
    setSearch('')
    setRange(null)
    getTransactions({
      status: undefined,
      search: '',
      range: null,
      paymentMode: undefined,
      gateway: undefined,
      rail: undefined
    })
  }

  const [timelineTxn, setTimelineTxn] = useState(null)
  const [logOpen, setLogOpen] = useState(false)
  const [refundTxn, setRefundTxn] = useState(null)

  const onViewConversation = record =>
    props.history.push(`/duty-assistant/conversations/${record.conversationId}`)

  // Loaded-page counts (the Revenue card uses the backend overall/date-filtered total instead).
  const successful = transactions.filter(t => t.status === 'SUCCESSFUL')
  const dated = range && (range[0] || range[1])

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space
            style={{ width: '100%', justifyContent: 'space-between' }}
            wrap
          >
            <div>
              <h3 style={{ marginBottom: 4 }}>
                Payments {meta && `(${meta.count})`}
              </h3>
              <p style={{ color: '#888', marginBottom: 16 }}>All payments</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setLogOpen(true)}
            >
              Log payment
            </Button>
          </Space>

          {/* Summary */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={8}>
              <Card size="small">
                <Statistic title="Loaded payments" value={transactions.length} />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Successful (loaded)"
                  value={successful.length}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small">
                <Statistic
                  title={dated ? 'Revenue (successful, filtered)' : 'Revenue (all successful)'}
                  value={formatMoney(summary.revenue, summary.currency || 'GHS')}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Filter bar */}
          <Space wrap style={{ marginBottom: 16 }}>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Payment mode"
              style={{ width: 190 }}
              value={paymentMode}
              onChange={setPaymentMode}
              options={PAYMENT_MODE_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Gateway"
              style={{ width: 160 }}
              value={gateway}
              onChange={setGateway}
              options={GATEWAY_OPTIONS}
            />
            <Select
              allowClear
              placeholder="Way of paying"
              style={{ width: 170 }}
              value={rail}
              onChange={setRail}
              options={RAIL_OPTIONS}
            />
            <RangePicker
              showTime
              value={range}
              onChange={setRange}
              placeholder={['From', 'To']}
            />
            <Input.Search
              allowClear
              placeholder="Search reference"
              style={{ width: 220 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={applyFilters}
            />
            <Button type="primary" onClick={applyFilters}>
              Apply
            </Button>
            <Button onClick={clearFilters}>Clear</Button>
            <Button icon={<ReloadOutlined />} onClick={applyFilters}>
              Refresh
            </Button>
          </Space>

          <Table
            rowKey="id"
            dataSource={transactions}
            columns={columns(onViewConversation, setTimelineTxn, setRefundTxn)}
            scroll={{ x: 1000 }}
            pagination={false}
            locale={{
              emptyText: <Empty description="No duty payments found" />
            }}
          />
          {transactions.length ? (
            <PaginationContainer
              meta={meta}
              getData={url =>
                getTransactions({ status, search, range, paymentMode, gateway, rail }, url)
              }
            />
          ) : null}
        </div>
      </div>

      <PaymentTimelineModal
        transaction={timelineTxn}
        open={!!timelineTxn}
        onClose={() => setTimelineTxn(null)}
      />

      <LogPaymentModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onLogged={() => applyFilters()}
      />

      <RefundModal
        open={Boolean(refundTxn)}
        transaction={refundTxn}
        onClose={() => setRefundTxn(null)}
        onDone={() => applyFilters()}
      />
    </PageLoader>
  )
}

TransactionsList.propTypes = {
  history: PropTypes.object
}

const TransactionsRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={TransactionsList} />
  </RouterSwitch>
)

TransactionsRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(TransactionsRoutes)
